import React from 'react';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Users, TrendingUp, UserPlus, Car, Calendar, ShieldCheck } from 'lucide-react';

function formatDateIST(raw) {
  if (!raw) return '-';
  try {
    const d = new Date(typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') + 'Z' : raw);
    if (isNaN(d.getTime())) return typeof raw === 'string' ? raw.slice(0, 16) : '-';
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return typeof raw === 'string' ? raw.slice(0, 16) : '-'; }
}

export function AnalyticsDetailModals({
  visitors,
  analytics,
  activeStaffCount,
  activeStaffList,
  vehicles,
  facilities,
  residentEntries = [],
  showVisitorsModal,
  setShowVisitorsModal,
  showActiveVisitorsModal,
  setShowActiveVisitorsModal,
  showActiveStaffModal,
  setShowActiveStaffModal,
  showVehiclesModal,
  setShowVehiclesModal,
  showFacilitiesModal,
  setShowFacilitiesModal,
  showResidentEntriesModal,
  setShowResidentEntriesModal
}) {
  // Active visitors (guests still on premises)
  const activeVisitors = visitors.filter(v => v.status === 'approved' && !v.exit_time);
  // Active resident entries (vehicle checked in, no exit)
  const activeResidentEntries = residentEntries.filter(e => !e.exit_time);

  return (
    <>
      <Dialog open={showVisitorsModal} onOpenChange={setShowVisitorsModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              All Visitors ({visitors.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {visitors.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No visitors recorded</p>
            ) : (
              visitors.slice(0, 50).map((visitor) => (
                <div key={visitor.id} className="p-3 bg-slate-50 rounded-lg border">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{visitor.name}</p>
                      <p className="text-sm text-slate-600">Villa: {visitor.villa_number}</p>
                      <p className="text-xs text-slate-500">Purpose: {visitor.purpose}</p>
                      <p className="text-xs text-slate-400">
                        Entry: {formatDateIST(visitor.entry_time)}
                      </p>
                    </div>
                    <Badge variant={visitor.status === 'approved' ? 'default' : visitor.status === 'completed' ? 'secondary' : 'outline'}>
                      {visitor.status}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showActiveVisitorsModal} onOpenChange={setShowActiveVisitorsModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Currently Active ({activeVisitors.length + activeResidentEntries.length})
            </DialogTitle>
            <DialogDescription>Visitors and resident vehicles currently on premises</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {activeVisitors.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Visitors ({activeVisitors.length})</p>
                {activeVisitors.map((visitor) => (
                  <div key={visitor.id} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 mb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{visitor.name}</p>
                        <p className="text-sm text-slate-600">Villa: {visitor.villa_number}</p>
                        <p className="text-xs text-slate-500">Purpose: {visitor.purpose}</p>
                        <p className="text-xs text-slate-400">Since: {formatDateIST(visitor.entry_time)}</p>
                      </div>
                      <Badge className="bg-emerald-600">Visitor</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeResidentEntries.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Resident Vehicles ({activeResidentEntries.length})</p>
                {activeResidentEntries.map((entry, i) => (
                  <div key={entry.id || `re-${i}`} className="p-3 bg-sky-50 rounded-lg border border-sky-200 mb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono font-bold">{entry.plate}</p>
                        <p className="text-sm text-slate-600">{entry.owner_name} — Villa {entry.villa_number}</p>
                        <p className="text-xs text-slate-400">Since: {formatDateIST(entry.entry_time || entry.timestamp || entry.created_at)}</p>
                        {entry.source && <p className="text-xs text-slate-400">Via: {entry.source === 'anpr' ? 'ANPR Camera' : 'Guard Verify'}</p>}
                      </div>
                      <Badge className="bg-sky-600">Resident</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeVisitors.length === 0 && activeResidentEntries.length === 0 && (
              <p className="text-center text-slate-500 py-8">No one currently on premises</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showActiveStaffModal} onOpenChange={setShowActiveStaffModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-gk-teal" />
              Active Staff on Campus ({activeStaffCount})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {activeStaffList.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No staff currently on premises</p>
            ) : (
              activeStaffList.map((entry) => (
                <div key={entry.id} className="p-3 bg-gk-bg rounded-lg border border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{entry.staff_name}</p>
                      <p className="text-sm text-slate-600">Villa: {entry.villa_number}</p>
                      <p className="text-xs text-slate-500">Entry Pass: {entry.entry_pass_number}</p>
                      <p className="text-xs text-slate-400">
                        Check-in: {formatDateIST(entry.check_in_time)}
                      </p>
                    </div>
                    <Badge className="bg-gk-teal">Active</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVehiclesModal} onOpenChange={setShowVehiclesModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-gk-teal" />
              Registered Vehicles ({vehicles.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {vehicles.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No vehicles registered</p>
            ) : (
              vehicles.map((vehicle) => (
                <div key={vehicle.id} className="p-3 bg-gk-bg rounded-lg border border-slate-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-mono font-bold text-lg">{vehicle.plate_number}</p>
                      <p className="text-sm text-slate-600">{vehicle.owner_name}</p>
                      <p className="text-xs text-slate-500">Villa: {vehicle.villa_number}</p>
                    </div>
                    <Badge className="bg-gk-teal">{vehicle.vehicle_type}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showFacilitiesModal} onOpenChange={setShowFacilitiesModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              All Facilities ({facilities.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {facilities.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No facilities added</p>
            ) : (
              facilities.map((facility) => (
                <div key={facility.id} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{facility.name}</p>
                      <p className="text-sm text-slate-600">{facility.description}</p>
                      <p className="text-xs text-slate-500">
                        Capacity: {facility.capacity} | Type: {facility.booking_type === 'date_range' ? 'Date Range' : 'Time Slots'}
                      </p>
                      <p className="text-xs text-slate-400">
                        Bookings: {facility.bookings?.length || 0} | Blocked: {facility.blocked_slots?.length || 0}
                      </p>
                    </div>
                    <Badge className="bg-amber-600">{facility.booking_type === 'date_range' ? 'Guest Room' : 'Facility'}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showResidentEntriesModal} onOpenChange={setShowResidentEntriesModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="resident-entries-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-gk-teal" />
              Resident Vehicle Entries ({residentEntries.length})
            </DialogTitle>
            <DialogDescription>Vehicle check-in records for registered residents</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {residentEntries.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No resident entries recorded</p>
            ) : (
              residentEntries.slice(0, 50).map((entry, i) => (
                <div key={entry.id || `entry-${i}`} className="p-3 bg-gk-bg rounded-lg border border-slate-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-mono font-bold text-lg">{entry.plate}</p>
                      <p className="text-sm text-slate-600">{entry.owner_name || 'Unknown'} - Villa {entry.villa_number || 'N/A'}</p>
                      <p className="text-xs text-slate-400">
                        {formatDateIST(entry.entry_time || entry.timestamp || entry.created_at)}
                      </p>
                      {entry.source && <p className="text-xs text-slate-400">Via: {entry.source === 'anpr' ? 'ANPR Camera' : 'Guard Verify'}</p>}
                    </div>
                    <Badge className={entry.decision === 'GREEN' ? 'bg-emerald-600' : 'bg-red-600'}>
                      {entry.decision}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
